import helper from '../helper'

const AUTOSAVE_DELAY_MS = 3000

export default {
  data(){
    return {
      draftAutosaveEligible: false,
      draftAutosaveReady: false,
      draftAutosaveTimer: null,
      draftAutosavePromise: null,
      draftAutosaveLastSnapshot: null,
      draftAutosaveState: 'idle',
      draftAutosaveClosing: false
    }
  },
  computed: {
    isDraftAutosaveEnabled(){
      return this.draftAutosaveEligible
    },
    draftAutosaveStatusLabel(){
      if(this.draftAutosaveState === 'saving') return this.$t('saving')
      if(this.draftAutosaveState === 'saved') return this.$t('autosave_saved')
      if(this.draftAutosaveState === 'error') return this.$t('autosave_failed')
      return this.$t('autosave_pending')
    }
  },
  watch: {
    localData: {
      deep: true,
      handler(){
        if(this.draftAutosaveReady && this.draftAutosaveEligible){
          this.scheduleDraftAutosave()
        }
      }
    }
  },
  methods: {
    draftAutosavePayload(){
      const payload = JSON.parse(JSON.stringify(this.localData || {}))
      delete payload.status
      delete payload.client
      delete payload.transactions
      delete payload.amount_paid

      payload.products = (this.localData.products || []).map((product, position) => {
        const customProduct = product.custom_product === true
        return {
          id: product.id || null,
          inventory_id: customProduct ? null : (product.inventory_id || null),
          product_id: customProduct ? null : (product.product_id || null),
          product_label: customProduct
            ? helper.sanitizeRichText(product.product_label || '')
            : String(product.product_label || product.label || ''),
          quantity: Number(product.quantity) || 0,
          price: Number(product.price) || 0,
          purchase_price: customProduct ? -1 : (Number(product.purchase_price) || 0),
          discount: Number(product.discount) || 0,
          vat: Number(product.vat) || 0,
          total: Number(product.total) || 0,
          sub_total: Number(product.sub_total) || 0,
          unity: product.unity || null,
          custom_product: customProduct,
          position
        }
      })

      return payload
    },
    draftAutosaveSnapshot(){
      return JSON.stringify(this.draftAutosavePayload())
    },
    startDraftAutosave(createdServerDraft, eligible = true){
      this.draftAutosaveEligible = eligible
      if(!eligible) return

      this.draftAutosaveLastSnapshot = createdServerDraft ? null : this.draftAutosaveSnapshot()
      this.$nextTick(() => {
        this.draftAutosaveReady = true
        if(createdServerDraft) this.scheduleDraftAutosave()
      })
    },
    resumeDraftAutosave(){
      if(!this.draftAutosaveEligible) return
      this.draftAutosaveReady = true
      if(this.draftAutosaveSnapshot() !== this.draftAutosaveLastSnapshot){
        this.scheduleDraftAutosave()
      }
    },
    scheduleDraftAutosave(){
      if(!this.draftAutosaveReady || !this.localData?.id) return
      if(this.draftAutosaveTimer) clearTimeout(this.draftAutosaveTimer)

      const snapshot = this.draftAutosaveSnapshot()
      if(snapshot === this.draftAutosaveLastSnapshot){
        this.draftAutosaveState = 'saved'
        return
      }

      this.draftAutosaveState = 'pending'
      this.draftAutosaveTimer = setTimeout(() => {
        this.draftAutosaveTimer = null
        this.flushDraftAutosave()
      }, AUTOSAVE_DELAY_MS)
    },
    async flushDraftAutosave(force = false){
      if(this.draftAutosaveTimer){
        clearTimeout(this.draftAutosaveTimer)
        this.draftAutosaveTimer = null
      }
      if((!this.draftAutosaveReady && !force) || !this.localData?.id) return

      if(this.draftAutosavePromise){
        await this.draftAutosavePromise.catch(() => {})
        if(force) return this.flushDraftAutosave(true)
        return
      }

      const resource = this.draftAutosaveResource()
      if(!resource) return

      const payload = this.draftAutosavePayload()
      const snapshot = JSON.stringify(payload)
      if(snapshot === this.draftAutosaveLastSnapshot){
        this.draftAutosaveState = 'saved'
        return
      }

      this.draftAutosaveState = 'saving'
      this.draftAutosavePromise = helper.customPatch(
        `${process.env.VUE_APP_API}/${resource}/${this.localData.id}/autosave`,
        payload,
        { silent: true }
      )

      try{
        const response = await this.draftAutosavePromise
        const savedProducts = response?.data?.products || []
        savedProducts.forEach((product, index) => {
          if(this.localData.products[index]){
            this.$set(this.localData.products[index], 'id', product.id)
          }
        })
        this.draftAutosaveLastSnapshot = this.draftAutosaveSnapshot()
        this.draftAutosaveState = 'saved'
      }catch(error){
        this.draftAutosaveState = 'error'
      }finally{
        this.draftAutosavePromise = null
        if(this.draftAutosaveReady && this.draftAutosaveSnapshot() !== this.draftAutosaveLastSnapshot){
          this.scheduleDraftAutosave()
        }
      }
    },
    async pauseDraftAutosave(savePendingChanges = false){
      this.draftAutosaveReady = false
      if(this.draftAutosaveTimer){
        clearTimeout(this.draftAutosaveTimer)
        this.draftAutosaveTimer = null
      }
      if(this.draftAutosavePromise){
        await this.draftAutosavePromise.catch(() => {})
      }
      if(savePendingChanges){
        await this.flushDraftAutosave(true)
      }
    }
  },
  beforeDestroy(){
    if(this.draftAutosaveTimer) clearTimeout(this.draftAutosaveTimer)
  }
}
